import { isEmpty, pickBy, sum } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useList } from 'react-use';
import { useAppContext } from 'src/contexts/AppContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { getListingPriceCalculationsInEventInputFromGroupSettings } from 'src/contexts/InventoryEventDetailContext/InventoryEventDetailContext.utils';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { useListingGroupAutoPricePreview } from 'src/hooks/api/useListingGroupAutoPricePreview';
import { useMergedInventory } from 'src/tiles/ListingTilesV2/ListingTilesV2.hooks';
import { groupCompListingsBetweenOwnListings } from 'src/tiles/ListingTilesV2/ListingTilesV2.utils';
import { mapToSelectedSectionSettings } from 'src/utils/types/selectedSectionSettingsWithOptionals';
import {
  Event,
  IListingGroupItem,
  Listing,
  ListingGroup,
  ListingGroupAutoPriceSettings,
  SectionInfo,
  SelectedSectionSettings,
} from 'src/WebApiController';

export const useListingGroupVirtuoso = (subGroups: IListingGroupItem[]) => {
  const initialGroupCounts = useMemo(
    () => subGroups.map((group) => (group as ListingGroup).groupItems.length),
    [subGroups]
  );

  // By default expand the first sub group
  const [localGroupCounts, localGroupCountsMutation] = useList<number>(
    initialGroupCounts.map((_, index) =>
      index === 0 ? initialGroupCounts[index] : 0
    )
  );

  const onUpdateExpanded = useCallback(
    (groupIndex: number) => (expanded: boolean) => {
      const udpatedCount = expanded ? initialGroupCounts[groupIndex] : 0;
      localGroupCountsMutation.updateAt(groupIndex, udpatedCount);
    },
    [initialGroupCounts, localGroupCountsMutation]
  );

  const getGroupItemIndexFromItemIndex = useCallback(
    (itemIndex: number, groupIndex: number): number => {
      const prevGroups = localGroupCounts.slice(0, groupIndex);
      const prevSum = sum(prevGroups);
      return itemIndex - prevSum;
    },
    [localGroupCounts]
  );

  return {
    localGroupCounts,
    onUpdateExpanded,
    getGroupItemIndexFromItemIndex,
  };
};

export const useListingGroupCompListings = (
  event: Event,
  ownListings: Listing[],
  listingGroupPricingSettings: ListingGroupAutoPriceSettings | undefined,
  onSaveListingGroupPricingSettings: (
    selectedSectionSettings: SelectedSectionSettings | null
  ) => void,
  showAllInventory: boolean
) => {
  const { loginContext } = useAppContext();
  const { getUiCurrency } = useLocalizationContext();

  const { venueMapInfo } = useEventMapContext();

  // We want to SelectedSectionSettings from UI by making it hasChange = true
  const pricingUpdates = useMemo(() => {
    if (!listingGroupPricingSettings) {
      return undefined;
    }
    const currency = getUiCurrency(
      loginContext?.user?.activeAccount?.currencyCode
    ).code;
    const {
      compListingSelectedSectionSettings,
      ...settingsWithoutSelectedSection
    } = listingGroupPricingSettings;

    return getListingPriceCalculationsInEventInputFromGroupSettings(
      listingGroupPricingSettings,
      settingsWithoutSelectedSection,
      currency
    );
  }, [
    getUiCurrency,
    listingGroupPricingSettings,
    loginContext?.user?.activeAccount?.currencyCode,
  ]);

  const { listingCalculationsQuery: listingGroupPriceCalcQuery } =
    useListingGroupAutoPricePreview(
      event,
      ownListings.map((l) => l.id),
      pricingUpdates
    );

  const selectedSectionIds = useMemo<number[]>(() => {
    if (
      listingGroupPricingSettings?.compListingSelectedSectionSettings
        ?.sectionIdFilter &&
      listingGroupPricingSettings.compListingSelectedSectionSettings
        .sectionIdFilter.length > 0
    ) {
      return listingGroupPricingSettings.compListingSelectedSectionSettings
        .sectionIdFilter;
    }
    const sectionToSectionId = (venueMapInfo?.sections ?? []).reduce(
      (res, section) => {
        res[section.name] = section.id;
        return res;
      },
      {} as Record<string, number>
    );
    if (showAllInventory && listingGroupPriceCalcQuery?.data?.compListings) {
      return listingGroupPriceCalcQuery.data.compListings
        .map((l) => l.section)
        .map((name) => (name ? sectionToSectionId[name] : undefined))
        .filter((id: number | undefined): id is number => !!id);
    }

    return ownListings
      .map((l) => l.seating.sectionId)
      .filter((id: number | null | undefined): id is number => !!id);
  }, [
    listingGroupPriceCalcQuery?.data?.compListings,
    listingGroupPricingSettings?.compListingSelectedSectionSettings
      ?.sectionIdFilter,
    ownListings,
    showAllInventory,
    venueMapInfo?.sections,
  ]);

  const selectedSections = useMemo<SectionInfo[]>(() => {
    const distinctIds = new Set(selectedSectionIds);
    return (venueMapInfo?.sections ?? []).filter((s) => distinctIds.has(s.id));
  }, [selectedSectionIds, venueMapInfo?.sections]);

  const onSelectedSectionsChange = useCallback(
    (selectedSections: SectionInfo[]) => {
      let compListingSelectedSectionSettingsNew: SelectedSectionSettings | null =
        null;
      const sectionIdFilter = selectedSections.map((s) => s.id);
      const selectedRowIds = selectedSections
        .flatMap(({ rows }) => rows)
        .map(({ id }) => id);
      const rowIdFilter = [];

      const compListingSelectedSectionSettings =
        listingGroupPricingSettings?.compListingSelectedSectionSettings;
      if (isEmpty(compListingSelectedSectionSettings?.rowIdFilter)) {
        rowIdFilter.push(...selectedRowIds);
      } else {
        rowIdFilter.push(
          ...(compListingSelectedSectionSettings?.rowIdFilter?.filter((rowId) =>
            selectedRowIds.includes(rowId)
          ) ?? [])
        );
      }
      const sectionRowIdFilter = pickBy(
        Object.assign(
          {},
          compListingSelectedSectionSettings?.sectionRowIdFilter ?? {}
        ),
        (_, key) => sectionIdFilter.includes(parseInt(key))
      );
      compListingSelectedSectionSettingsNew = {
        ...compListingSelectedSectionSettings,
        sectionIdFilter,
        rowIdFilter,
        sectionRowIdFilter,
      } as SelectedSectionSettings;

      const sanitizedSelectedSectionSettings =
        mapToSelectedSectionSettings(compListingSelectedSectionSettingsNew) ??
        null;

      onSaveListingGroupPricingSettings(sanitizedSelectedSectionSettings);
    },
    [
      listingGroupPricingSettings?.compListingSelectedSectionSettings,
      onSaveListingGroupPricingSettings,
    ]
  );

  const compListings = useMemo(() => {
    return listingGroupPriceCalcQuery?.data?.compListings ?? [];
  }, [listingGroupPriceCalcQuery?.data?.compListings]);

  const { allListingsMapped } = useMergedInventory(
    ownListings,
    compListings,
    false,
    showAllInventory
  );

  return {
    allInventoryWithGroups:
      groupCompListingsBetweenOwnListings(allListingsMapped),
    selectedSections,
    onSelectedSectionsChange,
    compListings,
  };
};
